import { z } from 'zod';
import { configureTile } from '@/extensions/configuration';
import { TileType } from '@/models/TileTypes';
import { InputType, type TileDefinition } from './TileDefinition';
import { Side, TileSideIOType } from '@/schemas/tiles';
import { standardBuildAndConnect } from '@/models/logic/utils';
import { defaultTileGraphics, connectionGraphics } from './DefaultTileGraphics';
import { colors } from '@/styles';
import { pixelMask, pixelRect } from '@/utils/tilePixels';

const labelSchema = z.string().trim().min(1).max(80);
export function debugTimerTile(start: boolean): TileDefinition<{ label: string }> {
  const type = start ? TileType.DebugTimerStart : TileType.DebugTimerEnd;
  const ioSides = {
    [Side.TOP]: { type: TileSideIOType.NONE, forceConnection: false },
    [Side.RIGHT]: { type: TileSideIOType.NONE, forceConnection: false },
    [Side.BOTTOM]: { type: TileSideIOType.NONE, forceConnection: false },
    [Side.LEFT]: { type: TileSideIOType.INPUT, forceConnection: true },
  };
  const tile: TileDefinition<{ label: string }> = {
    type,
    name: start ? 'Timer Start' : 'Timer End',
    canRotate: true,
    stateSchema: z.object({ label: labelSchema.default('default') }),
    createDefaultState: () => ({ label: 'default' }),
    ioSides,
    buildAndConnectNodes: (data) => {
      const queue = standardBuildAndConnect(
        start ? 'DEBUG_TIMER_START' : 'DEBUG_TIMER_END',
        data,
        tile,
      );
      const node = data.graph.nodes.get(data.instance.logicNodeIds![Side.TOP]!);
      if (node) node.state.label = data.instance.state.label;
      return queue;
    },
    createContextMenuItems: ({ instance, chipEditorModel }) => [
      {
        label: `Timer label (${instance.state.label})`,
        action: async (takeInput) => {
          try {
            const label = await takeInput(
              InputType.STRING,
              'Timer label',
              instance.state.label,
              true,
              (value) => ({
                isValid: labelSchema.safeParse(value).success,
                errorMessage: 'Enter a label from 1 to 80 characters.',
              }),
            );
            configureTile(
              instance,
              chipEditorModel,
              { label: labelSchema.parse(label) },
              'Change timer label',
            );
          } catch {
            /* Cancel preserves the label. */
          }
        },
      },
    ],
    graphics: {
      type,
      draw(g, { meta, connections, context }) {
        defaultTileGraphics(g);
        connectionGraphics(
          g,
          colors.signalColor,
          colors.noSignalColor,
          meta,
          connections,
          ioSides,
          context,
        );
        pixelMask(
          g,
          [
            '..#####..',
            '.##...##.',
            '##.....##',
            '#...#...#',
            '#...###.#',
            '#.......#',
            '##.....##',
            '.##...##.',
            '..#####..',
          ],
          8,
          7,
          colors.noSignalColor,
        );
        if (start)
          pixelMask(g, ['#..', '##.', '###', '##.', '#..'], 11, 9, colors.signalColor);
        else pixelRect(g, 11, 10, 3, 3, colors.signalColor);
      },
    },
  };
  return tile;
}
